import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, BookOpen, BarChart3, Eye, Gauge, History, KeyRound,
  LayoutDashboard, Layers, MessageSquare, Receipt, Search,
  Shield, ShieldAlert, User, Users, X,
} from 'lucide-react';
import { useAuth } from '@/lib/auth/useAuth';
import { hasAnyRole } from '@/lib/auth/permissions';
import { ALL_CLIENT_ROLES } from '@/types/auth';
import { cn } from '@/lib/utils/cn';
import type { DashboardRole } from '@/types/auth';

interface Cmd {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  to: string;
  requiredRoles?: DashboardRole[];
  group: string;
}

const ADMIN_ROLES: DashboardRole[] = [
  'AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN', 'AUTHMATECH_OPERATOR', 'AUTHMATECH_SUPPORT',
];
const FINANCE_ROLES: DashboardRole[] = ['AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN', 'AUTHMATECH_FINANCE'];

const ALL_COMMANDS: Cmd[] = [
  // Client portal
  { id: 'portal-overview',  label: 'Overview',         description: 'My workspace command center',  icon: LayoutDashboard, to: '/portal/overview',      requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  { id: 'portal-usage',     label: 'Usage Analytics',  description: 'API usage and trends',         icon: BarChart3,       to: '/portal/usage',         requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  { id: 'portal-fraud',     label: 'Fraud Protection', description: 'Risk decisions and events',    icon: ShieldAlert,     to: '/portal/fraud',         requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  { id: 'portal-trust',     label: 'Trust Scores',     description: 'Identity trust assessment',    icon: Eye,             to: '/portal/trust',         requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  { id: 'portal-risk',      label: 'Risk Events',      description: 'All Shield/Detect/Trust events',icon: Shield,         to: '/portal/risk-events',   requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  { id: 'portal-products',  label: 'My Products',      description: 'Enabled product features',     icon: Layers,          to: '/portal/products',      requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  { id: 'portal-sub',       label: 'Subscription',     description: 'Credits and balance',          icon: Receipt,         to: '/portal/subscription',  requiredRoles: ['AUTHMATECH_CLIENT_ADMIN', 'AUTHMATECH_CLIENT_FINANCE'], group: 'Client Portal' },
  { id: 'portal-docs',      label: 'Developer Docs',   description: 'API reference and credentials',icon: BookOpen,        to: '/portal/docs',          requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  { id: 'portal-requests',  label: 'My Requests',      description: 'Support and account requests', icon: MessageSquare,   to: '/portal/requests',      requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  { id: 'portal-account',   label: 'My Account',       description: 'Profile and API credentials',  icon: User,            to: '/my-account',           requiredRoles: ALL_CLIENT_ROLES, group: 'Client Portal' },
  // Admin
  { id: 'admin-overview',   label: 'Admin Overview',   description: 'Platform KPIs and activity',   icon: Gauge,           to: '/overview',             requiredRoles: ADMIN_ROLES, group: 'Admin' },
  { id: 'admin-clients',    label: 'Clients',          description: 'Manage all clients',            icon: Users,           to: '/clients',              requiredRoles: ADMIN_ROLES, group: 'Admin' },
  { id: 'admin-risk',       label: 'Risk Events',      description: 'All risk assessments',          icon: ShieldAlert,     to: '/risk-events',          requiredRoles: ADMIN_ROLES, group: 'Admin' },
  { id: 'admin-analytics',  label: 'Usage Analytics',  description: 'Platform usage trends',         icon: BarChart3,       to: '/analytics',            requiredRoles: FINANCE_ROLES, group: 'Admin' },
  { id: 'admin-verif',      label: 'Verifications',    description: 'All verification requests',     icon: History,         to: '/verifications',        requiredRoles: ADMIN_ROLES, group: 'Admin' },
  { id: 'admin-billing',    label: 'Billing',          description: 'Invoices and finance',          icon: Receipt,         to: '/billing',              requiredRoles: FINANCE_ROLES, group: 'Admin' },
  { id: 'admin-security',   label: 'Security & Access',description: 'Roles and access control',      icon: Shield,          to: '/security',             requiredRoles: ['AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN'], group: 'Admin' },
  { id: 'admin-apikeys',    label: 'API & Webhooks',   description: 'API key management reference',  icon: KeyRound,        to: '/settings/api-keys',   requiredRoles: ['AUTHMATECH_SUPER_ADMIN', 'AUTHMATECH_ADMIN'], group: 'Admin' },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRoles = user?.roles ?? [];
  const isClient = hasAnyRole(userRoles, ALL_CLIENT_ROLES);

  const visible = ALL_COMMANDS.filter((cmd) => {
    if (cmd.requiredRoles && !hasAnyRole(userRoles, cmd.requiredRoles)) return false;
    // For client users, only show client portal commands
    if (isClient && cmd.group === 'Admin') return false;
    if (!isClient && cmd.group === 'Client Portal') return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      cmd.label.toLowerCase().includes(q) ||
      (cmd.description?.toLowerCase().includes(q) ?? false)
    );
  });

  // Group visible commands
  const grouped = visible.reduce<Record<string, Cmd[]>>((acc, cmd) => {
    (acc[cmd.group] ??= []).push(cmd);
    return acc;
  }, {});

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, visible.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter') {
      const cmd = visible[activeIdx];
      if (cmd) { navigate(cmd.to); onClose(); }
    }
  }

  function go(cmd: Cmd) {
    navigate(cmd.to);
    onClose();
  }

  if (!open) return null;

  let flatIdx = 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] sm:pt-[15vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

      {/* Panel */}
      <div className="relative w-full max-w-lg animate-scale-in overflow-hidden rounded-2xl border border-border bg-surface shadow-card-lg">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3.5">
          <Search className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            placeholder="Navigate to…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            className="flex-1 bg-transparent text-sm text-fg placeholder:text-fg-subtle outline-none"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="rounded-md p-0.5 text-fg-subtle hover:text-fg"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : (
            <span className="kbd">esc</span>
          )}
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto scrollbar-hidden py-2">
          {visible.length === 0 ? (
            <div className="py-10 text-center text-sm text-fg-muted">
              No results for &ldquo;{query}&rdquo;
            </div>
          ) : (
            Object.entries(grouped).map(([group, cmds]) => (
              <div key={group}>
                <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
                  {group}
                </div>
                {cmds.map((cmd) => {
                  const thisIdx = flatIdx++;
                  const isActive = activeIdx === thisIdx;
                  const Icon = cmd.icon;
                  return (
                    <button
                      key={cmd.id}
                      type="button"
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                        isActive ? 'bg-primary-500/10 text-fg' : 'text-fg-muted hover:bg-surface-subtle hover:text-fg',
                      )}
                      onClick={() => go(cmd)}
                      onMouseEnter={() => setActiveIdx(thisIdx)}
                    >
                      <div
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
                          isActive
                            ? 'bg-primary-500/20 text-primary-600 dark:text-primary-400'
                            : 'bg-surface-subtle text-fg-subtle',
                        )}
                      >
                        <Icon className="h-4 w-4" aria-hidden />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-fg truncate">{cmd.label}</p>
                        {cmd.description && (
                          <p className="text-xs text-fg-muted truncate">{cmd.description}</p>
                        )}
                      </div>
                      {isActive && (
                        <ArrowRight className="h-4 w-4 shrink-0 text-primary-500" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-4 py-2 flex items-center gap-3 text-[11px] text-fg-subtle">
          <span><span className="kbd">↑↓</span> navigate</span>
          <span><span className="kbd">↵</span> go</span>
          <span><span className="kbd">esc</span> close</span>
        </div>
      </div>
    </div>
  );
}
